import { useState, useEffect } from "react";
import { ref, onValue } from "firebase/database";

/**
 * Custom hook to fetch data from Firebase Realtime Database.
 * @param {object} database - The Firebase database instance.
 * @param {string} path - The path to the data in the database (e.g., '/').
 * @returns {{ data: any, loading: boolean, error: string | null }} - An object containing the fetched data, loading state, and any error.
 */
const useFirebaseData = (database, path) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Validate database instance
    if (!database) {
      console.error(
        "useFirebaseData Error: Firebase database object is undefined or null."
      );
      setError("Firebase not initialized. Please check your configuration.");
      setLoading(false);
      return;
    }

    // Basic validation for the path
    if (typeof path !== "string" || path.length === 0) {
      console.error("useFirebaseData Error: Invalid path provided.");
      setError("Invalid database path.");
      setLoading(false);
      return;
    }

    const dbRef = ref(database, path);
    console.log(`DEBUG: Firebase Realtime Database ref created for path: ${path}`);

    // Set up the real-time listener
    const unsubscribe = onValue(
      dbRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const fetchedData = snapshot.val();
          setData(fetchedData);
          setError(null); // Clear any previous errors on successful fetch
          console.log(`DEBUG: Data fetched successfully from ${path}:`, fetchedData);
        } else {
          setData(null); // Explicitly set to null if no data exists
          setError(null); // No error, just no data
          console.log(`DEBUG: No data found at path: ${path}`);
        }
        setLoading(false);
      },
      (dbError) => {
        console.error(
          `useFirebaseData Error: Failed to load data from path "${path}":`,
          dbError
        );
        setError(`Failed to load data from Firebase. Details: ${dbError.message}`);
        setLoading(false);
      }
    );

    // Cleanup function to unsubscribe from the listener
    return () => {
      console.log(`DEBUG: Cleaning up Firebase onValue listener for path: ${path}`);
      unsubscribe();
    };
  }, [database, path]); // Re-run if database instance or path changes

  return { data, loading, error };
};

export default useFirebaseData;